import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { BrandLogo } from './BrandMark'
import { getWhatsAppUrl } from '../utils/whatsapp'

type NavItem = {
  name: string
  href: string
  external?: boolean
}

const navigation: NavItem[] = [
  { name: 'Home', href: '/' },
  { name: 'Visa Support', href: '/visa-support' },
  { name: 'Packages', href: '/packages' },
  { name: 'VAIA Flex', href: 'https://vaiaflex.co.za/', external: true },
  { name: 'VAIA Rewards', href: 'https://vaia-rewards.co.za/', external: true },
  { name: 'Contact', href: '/contact' },
]

function NavItemLink({
  item,
  className,
  onClick,
}: {
  item: NavItem
  className: string
  onClick?: () => void
}) {
  if (item.external) {
    return (
      <a
        href={item.href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
      >
        {item.name}
      </a>
    )
  }

  return (
    <Link to={item.href} className={className} onClick={onClick}>
      {item.name}
    </Link>
  )
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { franchiseData } = useFranchiseData()
  const whatsappUrl = getWhatsAppUrl(franchiseData, 'Hi, I would like to plan a trip with VAIA World.')
  const brandName = franchiseData?.name || 'VAIA World'

  const isActive = (item: NavItem) => {
    if (item.external) return false
    if (item.href === '/') return location.pathname === '/'
    return location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
  }

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileMenuOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [mobileMenuOpen])

  const mobileMenu = mobileMenuOpen && typeof document !== 'undefined'
    ? createPortal(
        <div className="lg:hidden fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            className="absolute inset-0 bg-ink/40 border-0 cursor-pointer"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-cream px-5 py-6 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between gap-3">
              <Link to="/" className="flex items-center min-w-0" onClick={() => setMobileMenuOpen(false)}>
                <BrandLogo alt={brandName} className="h-12 w-auto max-w-[180px]" />
              </Link>
              <button
                type="button"
                className="p-2 text-forest shrink-0"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mt-8 space-y-2">
              {navigation.map((item) => (
                <NavItemLink
                  key={item.name}
                  item={item}
                  className={clsx(
                    'block px-4 py-3 rounded-xl text-base font-semibold',
                    isActive(item)
                      ? 'bg-forest text-paper'
                      : 'text-ink hover:bg-paper'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </div>
            <a
              href={whatsappUrl}
              target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
              rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="nav-cta mt-8 self-start"
              onClick={() => setMobileMenuOpen(false)}
            >
              WhatsApp Us
            </a>
          </div>
        </div>,
        document.body
      )
    : null

  return (
    <nav className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 sm:px-7 py-2.5 bg-cream/92 backdrop-blur-md border-b border-line">
      <Link to="/" className="flex items-center min-w-0">
        <BrandLogo alt={brandName} className="h-10 sm:h-12 md:h-14 w-auto max-w-[150px] sm:max-w-[200px] md:max-w-none" />
      </Link>

      <div className="hidden lg:flex gap-0.5 bg-paper border border-line rounded-full p-1">
        {navigation.map((item) => (
          <NavItemLink
            key={item.name}
            item={item}
            className={clsx(
              'px-2.5 xl:px-3.5 py-2 rounded-full text-[12px] xl:text-[13px] font-semibold transition-all duration-200 whitespace-nowrap',
              isActive(item)
                ? 'bg-forest text-paper'
                : 'text-ink-soft hover:text-forest'
            )}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <a
          href={whatsappUrl}
          target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
          rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="nav-cta hidden sm:inline-flex"
        >
          WhatsApp Us
        </a>
        <button
          type="button"
          className="lg:hidden text-forest p-1.5"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {mobileMenu}
    </nav>
  )
}
